// Zweite Ebene eines Skript-Absatzes: die `###`-Unterüberschriften in seinem Rumpf.
//
// Gemessen am 2026-08-19 führen 4 von 5 Bibliotheks-Skripten welche — ein „Intro" besteht aus
// `### Who`, `### Why`, `### What`. Ohne diese Ebene wäre so ein Absatz nur im Ganzen austauschbar,
// und für ein anderes „Why" müsste man ein fremdes „Who" mitnehmen.
//
// Zwillingsfassung von `split_subsections` / `join_subsections` in `db_web/sales_crm.py`. Zwei
// Fassungen mit Absicht: der Server teilt, um die Fassungen der anderen zu ERNTEN, die Oberfläche
// teilt und setzt zusammen, um sie AUSZUWÄHLEN — sonst bräuchte jeder Klick einen Serverweg.
// Die Fälle in `__tests__/scriptSubsections.test.ts` und in `tests/test_sales_crm_section_options.py`
// prüfen dieselben Beispiele; wer hier etwas ändert, ändert es dort mit.

export interface Unterabschnitt {
  /** Der Titel der `###`-Zeile. `null` = der Vorspann vor der ersten Unterüberschrift. */
  title: string | null;
  body: string;
}

// `####` bleibt absichtlich ungeschnitten: das ist die dritte Ebene und gehört zum Rumpf ihres
// Unterabschnitts. Würde sie hier zerlegt, käme ein Absatz anders wieder zusammen, als er hereinkam.
const SUB_RE = /^###(?!#)[ \t]*(.*)$/gm;

export function splitSubsections(body: string | null | undefined): Unterabschnitt[] {
  const text = body ?? '';
  const treffer = Array.from(text.matchAll(SUB_RE));
  if (treffer.length === 0) {
    return text.trim() ? [{ title: null, body: text.trim() }] : [];
  }
  const teile: Unterabschnitt[] = [];
  const vorspann = text.slice(0, treffer[0].index).trim();
  if (vorspann) teile.push({ title: null, body: vorspann });
  treffer.forEach((m, i) => {
    const start = m.index + m[0].length;
    const ende = i + 1 < treffer.length ? treffer[i + 1].index : text.length;
    teile.push({ title: (m[1] ?? '').trim(), body: text.slice(start, ende).trim() });
  });
  return teile;
}

export function joinSubsections(teile: Unterabschnitt[]): string {
  return teile
    .map((t) => {
      const titel = (t.title ?? '').trim();
      const rumpf = (t.body ?? '').trim();
      return titel ? `### ${titel}\n\n${rumpf}`.trimEnd() : rumpf;
    })
    .filter(Boolean)
    .join('\n\n')
    .trim();
}

/**
 * Der Schlüssel, unter dem dieselbe Unterüberschrift in verschiedenen Skripten zusammenfindet.
 * Muss `_norm_title` in `db_web/sales_crm.py` entsprechen — dort werden die Fassungen so abgelegt.
 */
export function subKey(title: string | null | undefined): string {
  return (title ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '');
}

/** Hat dieser Absatz überhaupt eine zweite Ebene? Ein einziger Teil ohne Titel ist keine. */
export function hatUnterabschnitte(body: string | null | undefined): boolean {
  return splitSubsections(body).some((t) => t.title);
}
